/**
 * Archive and restore for one judge prompt or case version. An archived version shows who
 * archived it and why, with Restore. A version that is not current can be archived, with
 * an optional reason that Claude reads before it proposes again. The current version
 * cannot be archived; the API refuses it too.
 */
import { Archive, ArchiveRestore } from 'lucide-react';
import { useState } from 'react';
import Button from '../../../components/Button';
import InlineErrorDisplay from '../../../components/InlineErrorDisplay';
import type { VersionArchive } from '../types/evalTypes';
import { formatDateTime, versionAuthor } from '../utils/evalFormat';

const MAX_REASON_CHARS = 2000;

interface VersionArchiveControlProps {
  archived: VersionArchive | undefined;
  isCurrent: boolean;
  currentUid: string | undefined;
  /** Both throw on failure. */
  onArchive: (reason: string) => Promise<void>;
  onRestore: () => Promise<void>;
}

export const VersionArchiveControl = ({ archived, isCurrent, currentUid, onArchive, onRestore }: VersionArchiveControlProps) => {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      setAsking(false);
      setReason('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The change failed.');
    } finally {
      setBusy(false);
    }
  };

  if (archived) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-800">
        <span>
          Archived {formatDateTime(archived.archivedAt)} by {versionAuthor({ createdBy: archived.archivedBy }, currentUid)}
          {archived.reason && <>: {archived.reason}</>}
        </span>
        <Button variant="outline" size="sm" icon={<ArchiveRestore size={13} />} onClick={() => void run(onRestore)} disabled={busy} loading={busy}>
          Restore
        </Button>
        {error && <InlineErrorDisplay message={error} onDismiss={() => setError(null)} />}
      </div>
    );
  }

  if (isCurrent) return null;

  if (!asking) {
    return (
      <Button variant="outline" size="sm" icon={<Archive size={13} />} onClick={() => setAsking(true)} title="Reject or hide this version. Old runs keep it.">
        Archive
      </Button>
    );
  }

  return (
    <div className="w-full space-y-2 rounded-lg border border-gray-300 p-2">
      <input
        value={reason}
        maxLength={MAX_REASON_CHARS}
        disabled={busy}
        autoFocus
        onChange={(event) => setReason(event.target.value)}
        placeholder="Why? Optional. Claude reads it before it proposes again."
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900"
      />
      {error && <InlineErrorDisplay message={error} onDismiss={() => setError(null)} />}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => setAsking(false)} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" icon={<Archive size={13} />} onClick={() => void run(() => onArchive(reason.trim()))} disabled={busy} loading={busy}>
          Archive version
        </Button>
      </div>
    </div>
  );
};

/** "Show archived (3)", only when there are some. */
export const ShowArchivedToggle = ({ count, checked, onChange }: { count: number; checked: boolean; onChange: (checked: boolean) => void }) =>
  count === 0 ? null : (
    <label className="inline-flex items-center gap-2 text-xs text-gray-700">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      Show archived ({count})
    </label>
  );
